import React, { FormEvent, useEffect, useState } from "react";
import {
  verifyDriverLicence,
  verifyNin,
  verifyVoterCard,
} from "services/verification.service";
import {
  NinVerificationInput,
  NinVerificationRes,
} from "types/verification.type";
import {
  KycStatus,
  UserProfile,
  VerificationCheck,
  VerificationInfo,
} from "types/user.type";
import { updateUserVerificationCheck } from "services/user.service";
import VerificationChecks from "./VericationChecks";

interface Props {
  docType: string;
  docNumber: string;
  userId: string;
  status: KycStatus;
  verifiedInfo: VerificationInfo;
  userProfile: UserProfile;
  userRole: string;
}
const IdentityVerification = ({
  docNumber,
  docType,
  userId,
  status,
  verifiedInfo,
  userProfile,
  userRole,
}: Props) => {
  const [identity, setIdentity] = useState<VerificationInfo | null>(
    verifiedInfo || null
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (verifiedInfo) setIdentity(verifiedInfo);
  }, [verifiedInfo]);

  const handleVoterCardVerification = async (vin: string) => {
    try {
      setLoading(true);

      const info = await verifyVoterCard(vin, userId, "user");
      const [firstName, _, lastName] = info.data.full_name?.split(" ");

      setIdentity({
        firstName: firstName,
        lastName: lastName,
        phoneNumber: info.data?.phone,
        // occupation: info?.data?.occupation,
        image: "",
        address: info?.data?.address,
        email: "",
        middleName: "",
        dob: info?.data?.date_of_birth,
        gender: info?.data?.gender,
        type: "",
      });
    } catch (error: any) {
      setErrorMessage(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDriverLicenceVerification = async (licenceNumber: string) => {
    try {
      setErrorMessage("");
      setLoading(true);
      const info = await verifyDriverLicence(licenceNumber, userId, "user");

      setIdentity({
        firstName: info?.data?.firstName,
        lastName: info?.data?.firstName,
        phoneNumber: "",
        // occupation: "",
        image: info?.data?.photo,
        address: "",
        email: "",
        dob: info?.data?.birthDate,
        gender: info?.data?.gender,
        type: "",
        middleName: "",
      });
    } catch (error: any) {
      setErrorMessage(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNinVerification = async (nin: string) => {
    try {
      setErrorMessage("");
      setLoading(true);
      const ninPayload: NinVerificationInput = {
        nin,
        type: "user",
      };
      const res = await verifyNin(ninPayload, userId);
      const info = res.data as NinVerificationRes;

      setIdentity({
        firstName: info?.first_name,
        lastName: info?.last_name,
        phoneNumber: info?.phone_number,
        // occupation: "",
        image: info?.image,
        // @ts-ignore
        address: info.address,
        // @ts-ignore,
        email: info.email,
        dob: info?.date_of_birth,
        gender: info?.gender,
        type: "",
        middleName: "",
      });
    } catch (error: any) {
      setErrorMessage(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDocVerification = async () => {
    const val = docNumber;

    if (!docNumber) {
      setErrorMessage("No identity document provided");
      return;
    }
    if (docType?.toLowerCase()?.includes("nin")) {
      handleNinVerification(val);
    }
    if (docType?.toLowerCase().includes("voters card")) {
      handleVoterCardVerification(val);
    }
    if (["drivers license", "driverlicense"].includes(docType?.toLowerCase())) {
      handleDriverLicenceVerification(val);
    }
  };

  const handleVerificationCheck = async (
    key: keyof VerificationCheck,
    value: boolean
  ) => {
    // @ts-ignore
    const payload: VerificationCheck = {
      ...userProfile?.user?.verificationCheck,
      [key]: value,
    };
    await updateUserVerificationCheck(userProfile?.user?._id, payload);
  };

  return (
    <React.Fragment>
      <div className="card flex-1 min-w-0">
        <div className="card-body">
          <h6 className=" text-15">Verification Information</h6>
          <p className="text-xs mb-8">
            Get verification details from user provided {docType || "document"}
          </p>

          <div className=" flex  flex-col lg:flex-row gap-8 ">
            <div className="w-full overflow-x-auto">
              <table data-stack className="w-full ltr:text-left rtl:ext-right">
                <thead>
                  <th className="py-2 text-left font-semibold ps-0" scope="row">
                    Label
                  </th>
                  <th className="py-2 text-left font-semibold ps-0" scope="row">
                    User Information
                  </th>
                  <th className="py-2 text-left font-semibold ps-0" scope="row">
                    Verification Information
                  </th>
                </thead>
                <tbody>
                  <tr>
                    <th
                      className="py-2 text-left font-semibold ps-0"
                      scope="row"
                    >
                      First Name
                    </th>
                    <td data-label="User provided" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {userProfile?.user?.firstName}
                    </td>
                    <td data-label="Verification" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {identity?.firstName}
                    </td>
                  </tr>

                  <tr>
                    <th
                      className="py-2 text-left font-semibold ps-0"
                      scope="row"
                    >
                      Last Name
                    </th>
                    <td data-label="User provided" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {userProfile?.user?.lastName}
                    </td>
                    <td data-label="Verification" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {identity?.lastName}
                    </td>
                  </tr>
                  <tr>
                    <th
                      className="py-2 text-left font-semibold ps-0"
                      scope="row"
                    >
                      Phone Numer
                    </th>
                    <td data-label="User provided" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {userProfile?.user?.phoneNumber}
                    </td>
                    <td data-label="Verification" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {identity?.phoneNumber}
                    </td>
                  </tr>
                  <tr>
                    <th
                      className="py-2 text-left font-semibold ps-0"
                      scope="row"
                    >
                      Email
                    </th>
                    <td
                      data-label="User provided"
                      className="py-2 text-left text-slate-500 dark:text-zink-200"
                    >
                      {userProfile?.user?.email}
                    </td>
                    <td
                      data-label="Verification"
                      className="py-2 text-left text-slate-500 dark:text-zink-200"
                    >
                      {identity?.email}
                    </td>
                  </tr>
                  <tr>
                    <th
                      className="py-2 text-left font-semibold ps-0"
                      scope="row"
                    >
                      Date of birth
                    </th>
                    <td data-label="User provided" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {userRole === "Mynda"
                        ? userProfile?.mynda?.dob
                        : userRole === "Employer"
                        ? userProfile?.employer?.dob
                        : ""}
                    </td>
                    <td
                      data-label="Verification"
                      className="py-2 text-left text-slate-500 dark:text-zink-200"
                    >
                      {identity?.dob}
                    </td>
                  </tr>
                  <tr>
                    <th
                      className="py-2 text-left font-semibold ps-0"
                      scope="row"
                    >
                      Gender
                    </th>
                    <td data-label="User provided" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {userRole === "Mynda"
                        ? userProfile?.mynda?.gender
                        : userRole === "Employer"
                        ? userProfile?.employer?.gender
                        : ""}
                    </td>
                    <td
                      data-label="Verification"
                      className="py-2 text-left text-slate-500 dark:text-zink-200"
                    >
                      {identity?.gender}
                    </td>
                  </tr>
                  <tr>
                    <th
                      className="py-2 text-left font-semibold ps-0"
                      scope="row"
                    >
                      Address
                    </th>
                    <td data-label="User provided" className="py-2 text-left text-slate-500 dark:text-zink-200">
                      {userRole === "Mynda"
                        ? userProfile?.mynda?.address
                        : userRole === "Employer"
                        ? userProfile?.employer?.address
                        : ""}
                    </td>
                    <td
                      data-label="Verification"
                      className="py-2 text-left text-slate-500 dark:text-zink-200"
                    >
                      {identity?.address}
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left py-2 font-semibold ps-0" scope="row">Photo</th>
                    <td data-label="User provided">
                      {userRole === "Mynda"
                        ? userProfile?.mynda?.profilePicture && (
                            <img
                              src={userProfile?.mynda?.profilePicture?.url}
                              alt=""
                              className="w-full max-w-[200px] h-auto aspect-square"
                            />
                          )
                        : userRole === "Employer"
                        ? userProfile?.employer?.profilePicture && (
                            <img
                              src={userProfile?.employer?.profilePicture?.url}
                              alt=""
                              className="w-full max-w-[200px] h-auto aspect-square"
                            />
                          )
                        : ""}
                    </td>
                    <td data-label="Verification">
                      {/* {identity?.image ||
                        (identity?.photo && ( */}
                      <img
                        src={`data:image/png;base64,${
                          identity?.image || identity?.photo
                        }`}
                        className=" w-full max-w-[150px] h-[150px]"
                        alt=""
                      />
                      {/* ))} */}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {errorMessage && (
            <p className="text-xs my-4 text-red-500">{errorMessage}</p>
          )}
          <div className="flex  items-center justify-between mt-[2rem]">
            {!verifiedInfo && (
              <button
                type="button"
                onClick={handleDocVerification}
                disabled={loading}
                className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                {loading ? "Processing..." : "Get Verification"}
              </button>
            )}
          </div>
        </div>
        <VerificationChecks
          userId={userProfile?.user._id!}
          check={userProfile?.user?.verificationCheck!}
        />
      </div>
    </React.Fragment>
  );
};

export default IdentityVerification;
